import axios from 'axios';
import Auth from '@/services/Auth';
import Analytics from '../services/Analytics';
import LocalStorage from '../services/LocalStorage';
import Logger from '../services/Logger';
import { isOrganicUser } from './user';
import FirebaseDatabase from '../services/FirebaseDatabase';
import config from '../config';
import { notifyAPIError } from '../services/ErrorMonitoring';
import pricingConstants from '../utils/constants/pricing';

async function getAllExperiments() {
  try {
    const value = await FirebaseDatabase.getValue('/experiments');
    if (value) {
      return value;
    }
  } catch (error) {
    Logger.error('Error fetching experiments', { error });
  }
  return null;
}

async function getActiveBackEndExperiments() {
  try {
    const options = {
      method: 'POST',
      url: `${config.api.auraServices}/experiments/list`,
      data: {
        activeOnly: true,
        targetApp: 'core',
        targetOnboarding: true,
        limit: 0,
      },
    };
    const response = await axios(options);
    if (response && response.data) {
      return response.data;
    }
  } catch (error) {
    notifyAPIError(error, {
      message: 'Failed to list active back-end experiments',
    });
  }
  return null;
}

// Assign experiments server-side for the webview (session-cookie auth).
async function assignWebviewExperiments(experimentNames = []) {
  if (!experimentNames.length) {
    return {};
  }
  try {
    const response = await axios({
      method: 'POST',
      url: `${config.api.auraServices}/experiments/assign?includeFlags=true`,
      data: experimentNames,
      headers: { 'Content-Type': 'application/json' },
    });
    const data = response?.data || {};
    const assignments = {};
    Object.keys(data).forEach((name) => {
      const result = data[name]?.result;
      if (typeof result === 'string') {
        assignments[name] = result;
      }
    });
    return assignments;
  } catch (error) {
    notifyAPIError(error, {
      message: 'Failed to assign webview experiments',
    });
    return {};
  }
}

function saveChosenExperimentsToAnalytics(chosenExp) {
  if (!chosenExp || !Object.keys(chosenExp).length) {
    return;
  }
  const mpObject = {};
  Object.keys(chosenExp).forEach((key) => {
    const localValue = LocalStorage.getItem(key);
    // Check if experiment was chosen previously
    if (localValue && localValue.chosen) {
      // Only save to Analytics if experiment was chosen for user
      mpObject[`Experiment: ${key}`] = chosenExp[key];
    }
  });
  Analytics.setSuperProperties(mpObject);
  Analytics.setPeopleProperties(mpObject);
}

function saveExperiments(chosenExp, id) {
  Logger.debug('Save chosen experiments', { chosenExp });
  if (!chosenExp || !Object.keys(chosenExp).length) {
    return;
  }
  if (id && !!Auth.getCurrentUser()) {
    FirebaseDatabase.updateValue(`users/${id}/experiments`, chosenExp).catch(
      (err) =>
        Logger.error('error updating user experiments', { code: err.code })
    );
  }
  const mpObject = {};
  Object.keys(chosenExp).forEach((key) => {
    mpObject[`Experiment: ${key}`] = chosenExp[key];
    LocalStorage.setItem(key, {
      chosen: chosenExp[key],
    });
  });
  Analytics.setSuperProperties(mpObject);
  Analytics.setPeopleProperties(mpObject);
}

function chooseExperimentValue(
  experiment,
  profile,
  attributionData,
  { appLocale }
) {
  let result = null;
  if (!experiment) return result;
  const {
    id: expName,
    targetPlatform,
    targetLocales,
    targetTrafficSource,
    active,
    preAssignedValue,
  } = experiment;
  const localValue = LocalStorage.getItem(expName);
  // if target platform does not match return
  if (targetPlatform && !targetPlatform.includes('web')) {
    return result;
  }
  // if target locale does not match return
  if (targetLocales) {
    if (!appLocale) {
      Logger.warn(
        `Locale not passed for locale specific experiment ${expName}`,
        { targetLocales, appLocale }
      );
      return result;
    }
    if (!targetLocales.split('|').includes(appLocale)) {
      return result;
    }
  }
  // if target traffic source does not match return
  if (targetTrafficSource) {
    if (!profile && !attributionData) {
      return result;
    }
    if (
      targetTrafficSource === 'organic' &&
      !isOrganicUser(profile || { attributionData })
    ) {
      return result;
    }
    if (
      targetTrafficSource === 'paid' &&
      isOrganicUser(profile || { attributionData })
    ) {
      return result;
    }
  }
  // if experiment active
  if (active && active.length > 1) {
    // if user already chosen, use that
    if (profile && profile.experiments && profile.experiments[expName]) {
      Logger.debug(
        `Experiment ${expName} already chosen in profile: ${profile.experiments[expName]}`
      );
      result = { result: profile.experiments[expName] };
    } else if (preAssignedValue) {
      Logger.debug(
        `Experiment ${expName} already chosen in UTM experiments: ${preAssignedValue}`
      );
      result = { result: preAssignedValue, chosen: preAssignedValue };
    } else if (localValue && localValue.chosen) {
      // if experiment already saved in local storage
      Logger.debug(
        `Experiment ${expName} already chosen in local storage: ${localValue.chosen}`
      );
      result = { chosen: localValue.chosen, result: localValue.chosen };
    } else {
      // if experiment not already chosen, choose a random one and save
      const choices = [];
      let i = 0;
      for (i = 0; i < active.length; i++) {
        choices.push(active.charAt(i));
      }
      const chosenValue = choices[Math.floor(Math.random() * choices.length)];
      result = { chosen: chosenValue, result: chosenValue };
      Logger.debug(`Experiment ${expName} assigned: `, result);
    }

    // if experiment ended
  } else {
    // Logger.debug(`Experiment ${expName} ended`);
    result = { result: active };
  }
  return result;
}

function chooseExperiments(
  experiments,
  expNames,
  profile,
  id,
  attributionData, // Used to assign targeted traffic experiments when profile is not available (onboarding experiments)
  { appLocale, preAssignedExperiments = {} } = {}
) {
  const results = {}; // Stores results for all the experiments
  const chosen = {}; // Stores experiments that were explicitly chosen for users and not experiments that have turned off or present previously in user profile
  if (
    experiments &&
    Object.keys(experiments).length &&
    expNames &&
    expNames.length
  ) {
    expNames.forEach((expName) => {
      const exp = experiments[expName];
      const preAssignedValue = preAssignedExperiments[expName];
      if (exp) {
        const value = chooseExperimentValue(
          { ...exp, id: expName, preAssignedValue },
          profile,
          attributionData,
          { appLocale }
        );
        if (!value) return;
        const { result, chosen: chosenValue } = value;
        results[expName] = result;
        if (chosenValue) {
          chosen[expName] = chosenValue;
        }
      }
    });
  }
  saveExperiments(chosen, id);
  return results;
}

function chooseExperimentsList(
  experiments,
  profile,
  id,
  attributionData, // Used to assign targeted traffic experiments when profile is not available (onboarding experiments)
  { appLocale } // used to assign locale specific experiments
) {
  const results = {}; // Stores results for all the experiments
  const chosen = {}; // Stores experiments that were explicitly chosen for users and not experiments that have turned off or present previously in user profile
  if (experiments && experiments.length) {
    experiments.forEach((experiment) => {
      if (experiment && experiment.id) {
        const { id: expName } = experiment;
        const value = chooseExperimentValue(
          experiment,
          profile,
          attributionData,
          { appLocale }
        );
        if (!value) return;
        const { result, chosen: chosenValue } = value;
        results[expName] = result;
        if (chosenValue) {
          chosen[expName] = chosenValue;
        }
      }
    });
  }
  saveExperiments(chosen, id);
  return results;
}

// Resolve the LEGACY yearly-price variant for a user (PROD-2762), in priority
// order. Its letters mean the webYearlyPricing / mobileYearlyPricing prices
// (a = $79.99, c = $89.99, control = $89.99 since webYearlyPricing's 'c' won);
// webYearlyPricing2 letters are read by getWebYearlyPricing2Variant instead and
// take precedence — see getWebYearlyPriceKey.
//   1. `webYearlyPriceVariant` — the per-user flag the web checkout persisted;
//      honor it so the price matches what the user saw on web.
//   2. `experiments.mobileYearlyPricing` — the per-user assignment the Aura
//      mobile app persisted to `users/{id}/experiments/mobileYearlyPricing`.
//   3. `globalActive` — the `active` value of the `/experiments/mobileYearlyPricing`
//      RTDB node, but ONLY when it is a single forced variant (e.g. "a"). This
//      covers experiment wind-down: when the winner is rolled out by setting
//      `active` to one letter, users who were never individually bucketed still
//      get that price. It mirrors what the native app's ExperimentHelper does
//      for an ended experiment (`results[exp] = exp.active`).
//
// The single-letter guard is deliberate: a live split ("abc") is NEVER read
// here, so the webview never self-assigns or diverges from the native paywall —
// during a live experiment it relies purely on the per-user values above. The
// live `experiments.webYearlyPricing` assignment is likewise never read (per
// Rohan via Farhan, Slack 2026-07-02/07: the live field disappears once the
// experiment ends, whereas the persisted flag stays; tried in PR #2243 and
// rejected).
function getWebYearlyPricingVariant(user, globalActive) {
  const forcedVariant =
    typeof globalActive === 'string' && globalActive.length === 1
      ? globalActive
      : null;
  return (
    user?.webYearlyPriceVariant ||
    user?.experiments?.mobileYearlyPricing ||
    forcedVariant
  );
}

// webYearlyPricing2 (PROD-4498): a = $99.99, b = $89.99 (control), c = $109.99.
// Same contract as getWebYearlyPricingVariant — the webview never self-assigns,
// so it reads the `webYearlyPriceVariant2` flag the web checkout persisted, then
// a single-letter global `active` as the forced-winner fallback.
// No forced-winner fallback, deliberately — see the rule on
// getWebFamilyPlanVariant below. mobileYearlyPricing2 sits directly beneath this
// in getWebYearlyPriceKey and answers for anyone without the flag, so reading a
// concluded webYearlyPricing2 winner here would shadow the mobile arm the user
// was actually assigned.
function getWebYearlyPricing2Variant(user) {
  return user?.webYearlyPriceVariant2;
}

// mobileYearlyPricing2 (PROD-4608): the native paywall's own generation-2 yearly
// test — a $99.99, b $69.99 (control), c $109.99, d $89.99. Its letters are NOT
// webYearlyPricing2's; see MOBILE_YEARLY_PRICING_2_PRICE_KEYS. Unlike the web
// arms there is no `...PriceVariant2` flag to read:
// the mobile app writes its assignment to `users/{id}/experiments/mobileYearlyPricing2`,
// which is exactly what the webview reads here so a user who was quoted a price
// on the native paywall is charged that price when the webview takes the payment.
// Same single-letter forced-winner fallback and the same never-self-assign
// contract as every other reader in this file.
function getMobileYearlyPricing2Variant(user, globalActive) {
  const forcedVariant =
    typeof globalActive === 'string' && globalActive.length === 1
      ? globalActive
      : null;
  return user?.experiments?.mobileYearlyPricing2 || forcedVariant;
}

const WEB_YEARLY_PRICING_2_PRICE_KEYS = { a: '9999', b: '8999', c: '10999' };
// mobileYearlyPricing2 (PROD-4608). Steve, Slack 2026-08-25: hold control at
// today's $69.99 and carry $89.99 as a fourth arm, rather than raising control to
// match web. So this is NOT webYearlyPricing2's ladder and must not alias it —
// 'b' means $69.99 here and $89.99 there, and a shared map would silently charge
// mobile control an extra $20.
const MOBILE_YEARLY_PRICING_2_PRICE_KEYS = {
  a: '9999',
  b: '6999',
  c: '10999',
  d: '8999',
};
const LEGACY_WEB_YEARLY_PRICE_KEYS = { a: '7999', c: '8999' };
const DEFAULT_WEB_YEARLY_PRICE_KEY = '8999';

// Map a live webYearlyPricing2 assignment (the letter `useExperiments` returns)
// to a yearly price. Returns null for anything unassigned so callers can leave
// the pricing the page was opened with untouched.
function getWebYearlyPricing2PriceKey(variant) {
  return WEB_YEARLY_PRICING_2_PRICE_KEYS[variant] || null;
}

const WEB_FAMILY_PLAN_PRICE_KEYS = { a: '15999', b: '12999' };

// webFamilyPlanPricing2 (PROD-5890): a = $179.99, b = control at $159.99,
// c = $199.99. Gen 1's winner is this test's control, and that is exactly why
// this is its own map rather than a `c` added to the one above: 'a' is $159.99
// in gen 1 and $179.99 here. Aliasing them would quote gen-1 users the gen-2
// price. Same hazard the two yearly ladders carry, same answer.
const WEB_FAMILY_PLAN_2_PRICE_KEYS = { a: '17999', b: '15999', c: '19999' };

// mobileFamilyPlanPricing (PROD-4562): a = $159.99 / 6 members, b = control
// $119.99 / 4, c = $129.99 / 6. Its own family keys, not webFamilyPlanPricing's —
// the two share letters for a but b means different things (control $119.99/4 here
// vs $129.99/6 there), so a shared map would misprice control.
const MOBILE_FAMILY_PLAN_PRICE_KEYS = { a: '15999', b: '11999', c: '12999' };

// Keeps the single-letter forced-winner fallback, unlike the two web-side readers:
// nothing downstream answers once this test winds down, so without it an
// unassigned user would be stranded on the default family plan. Same reasoning as
// getMobileYearlyPricing2Variant. See getWebFamilyPlanVariant for the full rule.
function getMobileFamilyPlanVariant(user, globalActive) {
  const forcedVariant =
    typeof globalActive === 'string' && globalActive.length === 1
      ? globalActive
      : null;
  return user?.experiments?.mobileFamilyPlanPricing || forcedVariant;
}

// The family key for a mobileFamilyPlanPricing arm, or null when unassigned so
// callers keep whatever they would have shown.
function getMobileFamilyPlanPriceKey(variant) {
  return MOBILE_FAMILY_PLAN_PRICE_KEYS[variant] || null;
}

// The family key for a webFamilyPlanPricing2 arm, or null when unassigned so
// the gen-1 test below answers instead.
function getWebFamilyPlanPricing2PriceKey(variant) {
  return WEB_FAMILY_PLAN_2_PRICE_KEYS[variant] || null;
}

// Flag-only, no forced-winner fallback — the same rule getWebFamilyPlanVariant
// documents below. Gen 1 sits directly beneath this in getFamilyPriceKey and is
// what should answer for a user this test never assigned; reading a concluded
// gen-2 winner here would shadow the gen-1 arm they were actually quoted.
function getWebFamilyPlanPricing2Variant(user) {
  return user?.webFamilyPriceVariant2;
}

// webFamilyPlanPricing (PROD-4560): a = $159.99 family plan, b = control at
// $129.99. Returns null when unassigned so callers keep the $129.99 plan they
// would have shown anyway.
function getWebFamilyPlanPriceKey(variant) {
  return WEB_FAMILY_PLAN_PRICE_KEYS[variant] || null;
}

// Reads only the flag the web checkout persisted. That flag is a RECORD — "this
// user was quoted $X on web" — so honoring it protects them from being repriced.
// A forced winner is a different statement, "this is the price for everyone now",
// and feeding a concluded WEB experiment's winner to a mobile user conflates the
// two.
//
// The rule, which also covers getWebYearlyPricing2Variant above: keep a
// forced-winner fallback only where nothing downstream would otherwise answer.
//
//   getMobileYearlyPricing2Variant  keeps it — nothing below it but the baseline
//   getWebYearlyPricingVariant      keeps it — gen 1 is last in the chain
//   this + getWebYearlyPricing2Variant  drop it — the mobile experiments sit
//     directly beneath them and are what should answer for an unassigned user
//
// This also makes the webview agree with native, whose readers for both web-side
// experiments have always been flag-only (ExperimentHelper.getWebFamilyPlanVariant
// and getWebYearlyPricing2Variant). Before this they diverged the moment either
// web node was wound down to a single letter.
function getWebFamilyPlanVariant(user) {
  return user?.webFamilyPriceVariant;
}

// Whether the experiment is allowed to swap the plan a page currently holds.
// Only the standard yearly plan is its to replace — see
// EXPERIMENT_REPLACEABLE_YEARLY_IDS. This is deliberately keyed off the loaded
// plan rather than off referral/promo query params: the close-page follow-up
// offer swaps the plan in-app without touching the URL, and a referral link
// pasted without its query string looks like ordinary traffic, so a
// params-based check silently overwrote both.
function canWebYearlyPricing2Replace(pricingId) {
  return pricingConstants.EXPERIMENT_REPLACEABLE_YEARLY_IDS.includes(pricingId);
}

// The webview's single source of truth for which yearly price a user gets.
// webYearlyPricing2 wins; then mobileYearlyPricing2, so a user quoted a price on
// the native paywall is charged that same price when the webview takes the
// payment; users only ever bucketed by the previous generation keep the price
// that generation showed them; everyone else gets the $89.99 baseline that
// replaced $69.99 when webYearlyPricing's 'c' arm won.
//
// Web before mobile is the same precedence the native resolver uses
// (RNIapController.getYearlyProductId), and it has to stay that way in both
// places: a user can carry a web flag AND a mobile assignment, and the two
// surfaces disagreeing is exactly how someone gets shown one price and charged
// another. Generation 2 before generation 1 for the same reason — the letters
// are NOT interchangeable between generations ('a' is $79.99 in gen 1 and
// $99.99 in gen 2), so reading them in the wrong order silently misprices.
// The family-plan price chain, mirroring
// RNIapController.getFamilyPlanYearlyProductId term for term, in the same
// order, so both surfaces sell the same plan to the same user:
//
//   webFamilyPlanPricing2 → webFamilyPlanPricing → mobileFamilyPlanPricing
//   → webYearlyPricing2 pin → mobileYearlyPricing2 pin → default
//
// Only the web FAMILY flag outranks the family test — it is the one flag
// recording a family price the user actually saw. The yearly tests pin the
// family plan flat purely to keep family out of their own results, which is a
// control measure rather than a claim on what the family plan should cost, so
// both pins sit below it.
//
// Lives here rather than inline in the upsell page so it can be called by a
// test with a synthetic user and checked against the price it returns. The
// page-source ordering assertion this replaces could not see values at all:
// swapping 12999 and 11999 inside the two pins left the source order untouched
// and still passed, while mispricing every user who reached them. Raised by
// @devashishHp04. Both tests are kept — this one proves the webview charges
// correctly, the ordering one proves it still mirrors native.
//
// Returns a string on every branch. The inline version returned strings from
// the maps and numbers from the pins; harmless while the result only ever
// indexes an object, and a trap for the first caller to compare with ===.
function getFamilyPriceKey(
  user,
  { mobileFamilyPlanPricingActive, mobileYearlyPricing2Active } = {}
) {
  const webFamily2Key = getWebFamilyPlanPricing2PriceKey(
    getWebFamilyPlanPricing2Variant(user)
  );
  if (webFamily2Key) return String(webFamily2Key);

  const webFamilyKey = getWebFamilyPlanPriceKey(getWebFamilyPlanVariant(user));
  if (webFamilyKey) return String(webFamilyKey);

  const mobileFamilyKey = getMobileFamilyPlanPriceKey(
    getMobileFamilyPlanVariant(user, mobileFamilyPlanPricingActive)
  );
  if (mobileFamilyKey) return String(mobileFamilyKey);

  if (getWebYearlyPricing2Variant(user)) return '12999';

  return getMobileYearlyPricing2Variant(user, mobileYearlyPricing2Active)
    ? '11999'
    : '12999';
}

function getWebYearlyPriceKey(
  user,
  { mobileYearlyPricing2Active, mobileYearlyPricingActive } = {}
) {
  const priceKey = getWebYearlyPricing2PriceKey(
    getWebYearlyPricing2Variant(user)
  );
  if (priceKey) return priceKey;
  const mobilePriceKey =
    MOBILE_YEARLY_PRICING_2_PRICE_KEYS[
      getMobileYearlyPricing2Variant(user, mobileYearlyPricing2Active)
    ];
  if (mobilePriceKey) return mobilePriceKey;
  const legacyVariant = getWebYearlyPricingVariant(
    user,
    mobileYearlyPricingActive
  );
  return (
    LEGACY_WEB_YEARLY_PRICE_KEYS[legacyVariant] || DEFAULT_WEB_YEARLY_PRICE_KEY
  );
}

// Pure reader for the auraPaywallCTAPosition experiment. Mirrors native's
// chooseExperimentValue semantics: when the global `active` is a single letter
// the experiment is "forced" to that winner and applies to everyone regardless
// of any per-user assignment (native's `else { result = active }` branch). When
// `active` has >1 char the experiment is live, so fall back to the per-user
// value the mobile app persisted to users/<id>/experiments. The webview never
// self-assigns. Variant 'a' relocates the CTA to the paywall bottom.
function getPaywallCTAPositionVariant(user, globalActive) {
  if (typeof globalActive === 'string' && globalActive.length === 1) {
    return globalActive;
  }
  return user?.experiments?.auraPaywallCTAPosition || null;
}

// Reader for auraPaywallMoneyBackGuaranteeCard (PROD-3056 / PROD-3844), same
// shape as getPaywallCTAPositionVariant: a single-letter global `active` is a
// forced winner for everyone; otherwise fall back to the per-user assignment.
function getMoneyBackGuaranteeCardVariant(user, globalActive) {
  if (typeof globalActive === 'string' && globalActive.length === 1) {
    return globalActive;
  }
  return user?.experiments?.auraPaywallMoneyBackGuaranteeCard || null;
}

// Reader for auraPaywallAddFAQs (PROD-3057), same shape as
// getPaywallCTAPositionVariant: a single-letter global `active` is a forced
// winner for everyone; otherwise fall back to the per-user assignment.
function getPaywallAddFAQsVariant(user, globalActive) {
  if (typeof globalActive === 'string' && globalActive.length === 1) {
    return globalActive;
  }
  return user?.experiments?.auraPaywallAddFAQs || null;
}

// Reader for paywallBenefitsNewCopy (PROD-6366), same shape as
// getPaywallCTAPositionVariant: a single-letter global `active` is a forced
// winner for everyone; otherwise fall back to the per-user assignment. Variant
// 'a' swaps the whole benefits list for the Aura 4.0 Figma copy; anything else
// is control. Mirrors the native side, where the same experiment gates
// SubscriptionModal3.getBulletText() (Aura#7175) -- the two surfaces must agree,
// since a user can meet either paywall in the same funnel.
function getPaywallBenefitsNewCopyVariant(user, globalActive) {
  if (typeof globalActive === 'string' && globalActive.length === 1) {
    return globalActive;
  }
  return user?.experiments?.paywallBenefitsNewCopy || null;
}

// Two readers for the backend's experiments API. Named one per endpoint rather
// than taking a path, so a caller asks for the fact it wants and the URL, auth
// and timeout stay in one place -- the design gate below is the first caller of
// them, not the only one they are meant to serve.
//
// Both go through UserService rather than Firebase: the webview already reaches
// the backend for everything else here, and a second way into Firebase is a
// second thing to keep in step. Auth is the user-web token, the same bearer the
// `/auraServices` proxy injects for the protected routes it already forwards.
//
// Unlike the other API helpers in this file, these deliberately do NOT swallow
// their own errors. Their caller has to know which of the two reads failed so it
// can still decide on the other (see getAuraDesignArm), and a null returned at
// the source would erase that difference. Handle failure at the call site.
const EXPERIMENT_READ_TIMEOUT_MS = 1500;

async function readExperimentsApi(path) {
  const base = process.env.NEXT_PUBLIC_AURA_SERVICES_URL;
  if (!base) return null;
  const { data } = await axios.get(`${base}/${path}`, {
    timeout: EXPERIMENT_READ_TIMEOUT_MS,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${process.env.NEXT_PUBLIC_USER_WEB_TOKEN}`,
    },
  });
  return data ?? null;
}

// GET /experiments/:name -- the experiment's own row. `active` is the letter a
// concluded experiment was rolled out to and applies to everyone while it is a
// single character; the rest is targeting and lifecycle metadata.
async function fetchExperimentDetails(name) {
  return readExperimentsApi(`experiments/${encodeURIComponent(name)}`);
}

// GET /experiments/assignments/:userId -- every arm the app has bucketed this
// user into, keyed by experiment name. Returns the whole map rather than one
// letter: the endpoint answers for every experiment at once, so a caller that
// needs two of them should not pay for two round trips.
async function getUserAssignment(userId) {
  return readExperimentsApi(
    `experiments/assignments/${encodeURIComponent(userId)}`
  );
}

// Server-side read of the arm the app assigned this user, so the paywall page can
// render the right design before any JavaScript runs. Left to the client, the same
// decision was a race between a Firebase read and a 2.5s fallback, and a cold
// connection lost it.
//
// Anything that fails or stalls resolves null and the caller falls back to the
// current design at once -- never a delayed swap.
// The redesign gate, in three generations. Generation 1 is contaminated -- a
// client-side race served the control design to ~42% of its arm-A users
// (PROD-5994) -- so the app re-keyed to generation 2 to re-randomise cleanly.
// Generation 3 re-keys again to re-run the experiment on a fresh split: the app
// reuses any assignment already on the profile, so only a new key re-randomises.
//
// Generation 2 is NOT read. It concluded on 2026-09-29 at `active: 'b'`, and a
// single-letter `active` is one the app's ExperimentHelper never buckets against
// -- it only assigns when `active.length > 1`. So every build still keyed to
// generation 2 now renders legacy whatever is stored on the profile, and reading
// the stored assignment here would hand those users a 4.0 paywall behind a legacy
// app. Two generations are read: 3 for builds that have been re-keyed, 1 as the
// concluded floor underneath. If generation 2 is ever re-opened at a multi-letter
// `active`, its assignment has to be read again or the split reappears inverted.
const AURA_DESIGN_EXPERIMENT = 'auraDesignChange';
const AURA_DESIGN_EXPERIMENT_3 = 'auraDesignChange3';

// Kept as a switch rather than deleted: `auraDesignChange` is shared with the
// app, so the paywall can be turned off from here without touching its rollout.
const AURA_4_PAYWALL_ENABLED = true;

async function getAuraDesignArm(userId) {
  if (!userId) return null;
  try {
    // The experiment row carries the forced-winner `active`; the assignment
    // carries what the app bucketed this user into. Same precedence the client
    // reader applies, so one rule decides the design on both sides.
    // Settled, not all: the two reads answer different halves of the question and
    // either half can still decide it. A single-letter `active` is a concluded
    // experiment and wins on its own, so losing the assignments read must not
    // discard it -- and an assignment still decides when the config read is the
    // one that failed. `Promise.all` threw both away whenever either failed.
    const [experiment, assignments] = await Promise.allSettled([
      fetchExperimentDetails(AURA_DESIGN_EXPERIMENT),
      getUserAssignment(userId),
    ]).then((results) =>
      results.map((r) => (r.status === 'fulfilled' ? r.value : null))
    );

    // The newest generation the user's app has bucketed them into decides, since
    // that is the one their app reads. An assignment only, never the row: during
    // the rollout the generation-3 node sits dark at a single letter, and a single
    // letter is exactly what getAuraDesignChangeVariant treats as a forced winner
    // for everyone. Reading it would drop every user still on an older build onto
    // the legacy paywall the day this deploys. An assignment, by contrast, only
    // exists once the re-keyed build has bucketed the user, so it is self-gating:
    // inert until that build ships, decisive the moment it does.
    const assignedVariant3 = assignments?.[AURA_DESIGN_EXPERIMENT_3];
    if (assignedVariant3) {
      return assignedVariant3;
    }

    return getAuraDesignChangeVariant(
      {
        experiments: {
          auraDesignChange: assignments?.[AURA_DESIGN_EXPERIMENT],
        },
      },
      experiment?.active
    );
  } catch (error) {
    notifyAPIError(error, {
      message: 'Failed to resolve the auraDesignChange arm server-side',
    });
    return null;
  }
}

// The app writes the redesign arm to the user's Firebase profile at sign-up, about
// a dozen seconds before the experiments API has its copy, and it preloads the
// webview paywall inside that gap. So once the profile is loaded, its arm
// decides; the server-side read only covers the first paint and a profile that
// has no arm.
function shouldShowAuraRedesign(user, serverShowRedesign) {
  const profileArm = user?.experiments?.[AURA_DESIGN_EXPERIMENT_3];
  if (profileArm) {
    return profileArm === 'a';
  }
  return serverShowRedesign;
}

// Reader for auraDesignChange (PROD-5084), same shape as the paywall readers
// above: a single-letter global `active` is a forced winner for everyone;
// otherwise fall back to the per-user assignment the native app wrote.
//
// This is the app's own redesign flag rather than a web-only one, so a user the
// app put on Aura 4.0 gets the 4.0 webview too — the two can't disagree
// mid-funnel. 'a' = new design, 'b' = current design; the webview never assigns.
function getAuraDesignChangeVariant(user, globalActive) {
  if (typeof globalActive === 'string' && globalActive.length === 1) {
    return globalActive;
  }
  return user?.experiments?.auraDesignChange || null;
}

// Reader for removeYearlyDiscountCopy (PROD-3587 / PROD-3845), same shape as
// getPaywallCTAPositionVariant: a single-letter global `active` is a forced
// winner for everyone; otherwise fall back to the per-user assignment. Variant
// 'a' HIDES the "50% off" copy (Aura#6710); anything else is control.
function getRemoveYearlyDiscountCopyVariant(user, globalActive) {
  if (typeof globalActive === 'string' && globalActive.length === 1) {
    return globalActive;
  }
  return user?.experiments?.removeYearlyDiscountCopy || null;
}

// Reader for showRealYearlyDiscount (PROD-3896), same shape as
// getPaywallCTAPositionVariant: a single-letter global `active` is a forced
// winner for everyone; otherwise fall back to the per-user assignment. Variant
// 'a' keeps the discount pill but shows the real saving of the yearly plan
// against a year of the 6-month plan instead of the flat 50% claim (Aura#6745).
//
// Deliberately a separate experiment from removeYearlyDiscountCopy rather than
// a third arm of it: native's chooseAll reuses a user's stored assignment, so
// an arm added to an already-live test only fills from users who were never
// bucketed and never draws contemporaneously with the existing arms.
function getShowRealYearlyDiscountVariant(user, globalActive) {
  if (typeof globalActive === 'string' && globalActive.length === 1) {
    return globalActive;
  }
  return user?.experiments?.showRealYearlyDiscount || null;
}

export {
  AURA_4_PAYWALL_ENABLED,
  getActiveBackEndExperiments,
  getAllExperiments,
  assignWebviewExperiments,
  chooseExperiments,
  chooseExperimentsList,
  saveExperiments,
  saveChosenExperimentsToAnalytics,
  getWebYearlyPricingVariant,
  getWebYearlyPricing2Variant,
  getWebYearlyPricing2PriceKey,
  getMobileYearlyPricing2Variant,
  getMobileFamilyPlanPriceKey,
  getMobileFamilyPlanVariant,
  getWebFamilyPlanPriceKey,
  getWebFamilyPlanVariant,
  getWebFamilyPlanPricing2PriceKey,
  getWebFamilyPlanPricing2Variant,
  canWebYearlyPricing2Replace,
  getWebYearlyPriceKey,
  getFamilyPriceKey,
  getPaywallCTAPositionVariant,
  getMoneyBackGuaranteeCardVariant,
  getPaywallAddFAQsVariant,
  getPaywallBenefitsNewCopyVariant,
  getRemoveYearlyDiscountCopyVariant,
  getShowRealYearlyDiscountVariant,
  getAuraDesignChangeVariant,
  fetchExperimentDetails,
  getUserAssignment,
  getAuraDesignArm,
  shouldShowAuraRedesign,
};
